/**
 * Fantasy Console 0 - Console Context
 *
 * Provides global access to the console capture function for thumbnail generation.
 * Console components register their capture function when mounted.
 * Also provides global console control (reload, pause/resume, reset) for keyboard shortcuts,
 * and centralized error management for runtime errors.
 */

import {
  createContext,
  useContext,
  useRef,
  useCallback,
  useState,
  type ReactNode,
} from 'react'
import type { ErrorPayload } from '@fc0/types'
import type { RuntimeError } from '../components/CodeEditor'

type CaptureFunction = () => Promise<string | null>

export interface ActiveConsoleRef {
  reload: () => void
  pauseResume: () => void
  reset: () => void
}

interface ConsoleContextType {
  /** Register a capture function (called by console components when mounted) */
  registerCapture: (capture: CaptureFunction) => void
  /** Unregister the capture function (called by console components when unmounted) */
  unregisterCapture: () => void
  /** Capture the current console canvas as a data URL */
  captureCanvas: () => Promise<string | null>
  /** Register the active console for global control (reload, pause/resume) */
  registerActiveConsole: (ref: ActiveConsoleRef) => void
  /** Unregister the active console */
  unregisterActiveConsole: () => void
  /** Reload the currently active console */
  reload: () => void
  /** Toggle pause/resume on the currently active console */
  pauseResume: () => void
  /** Reset the currently active console */
  reset: () => void
  /** Current runtime errors */
  errors: RuntimeError[]
  /** Report a runtime error (deduplicates by message) */
  onError: (error: ErrorPayload) => void
  /** Clear all runtime errors */
  clearErrors: () => void
}

const ConsoleContext = createContext<ConsoleContextType | null>(null)

/**
 * Try to detect the source of an error from its stack trace
 */
function detectErrorSource(error: ErrorPayload): RuntimeError['source'] {
  const stack = error.stack || ''
  const message = error.message || ''

  if (message.includes('SyntaxError') || message.includes('Unexpected')) {
    return 'syntax'
  }
  if (stack.includes('_init')) {
    return 'init'
  }
  if (stack.includes('_update')) {
    return 'update'
  }
  if (stack.includes('_draw')) {
    return 'draw'
  }
  return 'runtime'
}

export function ConsoleProvider({ children }: { children: ReactNode }) {
  // Use ref to avoid stale closure issues - the callback always reads the current value
  const captureFunctionRef = useRef<CaptureFunction | null>(null)
  const activeConsoleRef = useRef<ActiveConsoleRef | null>(null)
  const [errors, setErrors] = useState<RuntimeError[]>([])

  const registerCapture = useCallback((capture: CaptureFunction) => {
    captureFunctionRef.current = capture
  }, [])

  const unregisterCapture = useCallback(() => {
    captureFunctionRef.current = null
  }, [])

  const captureCanvas = useCallback(async (): Promise<string | null> => {
    if (!captureFunctionRef.current) {
      console.warn('No console capture function registered')
      return null
    }
    return captureFunctionRef.current()
  }, [])

  const registerActiveConsole = useCallback((ref: ActiveConsoleRef) => {
    activeConsoleRef.current = ref
  }, [])

  const unregisterActiveConsole = useCallback(() => {
    activeConsoleRef.current = null
  }, [])

  const reload = useCallback(() => {
    activeConsoleRef.current?.reload()
  }, [])

  const pauseResume = useCallback(() => {
    activeConsoleRef.current?.pauseResume()
  }, [])

  const reset = useCallback(() => {
    activeConsoleRef.current?.reset()
  }, [])

  const onError = useCallback((error: ErrorPayload) => {
    setErrors((prev) => {
      if (prev.some((e) => e.message === error.message)) {
        return prev
      }
      const runtimeError: RuntimeError = {
        ...error,
        source: detectErrorSource(error),
        timestamp: Date.now(),
      }
      return [...prev, runtimeError]
    })
  }, [])

  const clearErrors = useCallback(() => {
    setErrors([])
  }, [])

  return (
    <ConsoleContext.Provider
      value={{
        registerCapture,
        unregisterCapture,
        captureCanvas,
        registerActiveConsole,
        unregisterActiveConsole,
        reload,
        pauseResume,
        reset,
        errors,
        onError,
        clearErrors,
      }}
    >
      {children}
    </ConsoleContext.Provider>
  )
}

export function useConsole(): ConsoleContextType {
  const context = useContext(ConsoleContext)
  if (!context) {
    throw new Error('useConsole must be used within a ConsoleProvider')
  }
  return context
}
